import { useRef, useMemo, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float, Sparkles, MeshDistortMaterial } from "@react-three/drei";
import * as THREE from "three";

// Tracks normalized pointer position via window listeners so the canvas
// itself can stay `pointer-events: none` and never block UI clicks.
const usePointer = () => {
  const pointer = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const handleMove = (e) => {
      pointer.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      pointer.current.y = -(e.clientY / window.innerHeight) * 2 + 1;
    };
    window.addEventListener("pointermove", handleMove);
    return () => window.removeEventListener("pointermove", handleMove);
  }, []);

  return pointer;
};

const ParticleField = ({ pointer }) => {
  const groupRef = useRef();
  const count = 900;

  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const radius = 6 + Math.random() * 6;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(Math.random() * 2 - 1);
      arr[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      arr[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta) * 0.6;
      arr[i * 3 + 2] = radius * Math.cos(phi) - 4;
    }
    return arr;
  }, []);

  useFrame((state, delta) => {
    if (!groupRef.current) return;
    groupRef.current.rotation.y += delta * 0.03;
    groupRef.current.rotation.x = THREE.MathUtils.lerp(
      groupRef.current.rotation.x,
      pointer.current.y * 0.15,
      0.02
    );
    groupRef.current.rotation.z = THREE.MathUtils.lerp(
      groupRef.current.rotation.z,
      pointer.current.x * 0.1,
      0.02
    );
  });

  return (
    <group ref={groupRef}>
      <points>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            args={[positions, 3]}
          />
        </bufferGeometry>
        <pointsMaterial
          size={0.045}
          color="#48B3BD"
          transparent
          opacity={0.55}
          sizeAttenuation
          depthWrite={false}
        />
      </points>
    </group>
  );
};

const GlassShape = ({ geometry, position, color, scale = 1, speed = 1 }) => (
  <Float speed={speed} rotationIntensity={1.1} floatIntensity={1.6}>
    <mesh position={position} scale={scale}>
      {geometry}
      <MeshDistortMaterial
        color={color}
        distort={0.35}
        speed={1.4}
        roughness={0.15}
        metalness={0.4}
        transparent
        opacity={0.85}
      />
    </mesh>
  </Float>
);

const Rig = ({ pointer }) => {
  useFrame((state) => {
    state.camera.position.x = THREE.MathUtils.lerp(
      state.camera.position.x,
      pointer.current.x * 0.6,
      0.03
    );
    state.camera.position.y = THREE.MathUtils.lerp(
      state.camera.position.y,
      pointer.current.y * 0.4,
      0.03
    );
    state.camera.lookAt(0, 0, 0);
  });
  return null;
};

const Scene3D = ({ className = "" }) => {
  const pointer = usePointer();

  return (
    <div className={`pointer-events-none ${className}`}>
      <Canvas
        dpr={[1, 1.5]}
        camera={{ position: [0, 0, 9], fov: 45 }}
        gl={{ antialias: true, alpha: true }}
        style={{ position: "absolute", inset: 0 }}
      >
        <ambientLight intensity={0.6} />
        <pointLight position={[5, 5, 5]} intensity={1.2} color="#ffffff" />
        <pointLight position={[-5, -3, -2]} intensity={0.8} color="#48B3BD" />

        <Rig pointer={pointer} />
        <ParticleField pointer={pointer} />

        <GlassShape
          geometry={<icosahedronGeometry args={[1, 0]} />}
          position={[3.4, 1.4, -2]}
          color="#48B3BD"
          scale={1.1}
          speed={1.2}
        />
        <GlassShape
          geometry={<torusGeometry args={[0.75, 0.28, 32, 100]} />}
          position={[-3.6, -1.2, -1.5]}
          color="#86CDD3"
          scale={1}
          speed={0.9}
        />
        <GlassShape
          geometry={<octahedronGeometry args={[0.7, 0]} />}
          position={[-2.4, 2, -3]}
          color="#ffffff"
          scale={0.85}
          speed={1.5}
        />

        <Sparkles
          count={60}
          scale={[10, 6, 6]}
          size={2.5}
          speed={0.3}
          color="#48B3BD"
          opacity={0.6}
        />
      </Canvas>
    </div>
  );
};

export default Scene3D;
